import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { MagnifyingGlass, Tray } from '@phosphor-icons/react';

/*
 * The one empty state. Replaces four forks and both illustration SVGs.
 *
 * Rule (audit 6.7): the RAIL owns "no records match" and offers the create
 * action. The DETAIL pane stays blank with a single line and never repeats the
 * action — pass `variant="detail"` for that.
 *
 * One oversized Phosphor glyph at 44px in neutral-300. No illustration, ever.
 */
const GLYPH = {
  records: Tray, // nothing exists yet
  results: MagnifyingGlass, // a filter or search matched nothing
};

function EmptyState({
  variant,
  glyph,
  statement,
  guidance,
  action,
  className,
}) {
  const Glyph = GLYPH[glyph] || GLYPH.records;

  if (variant === 'detail') {
    return (
      <div
        className={cn(
          'h-full flex items-center justify-center p-6 text-center',
          className,
        )}
      >
        <p className="text-sm text-neutral-500">{statement}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'h-full flex flex-col items-center justify-center p-8 text-center',
        className,
      )}
    >
      <Glyph size={44} weight="bold" className="text-neutral-300" />
      <p className="mt-4 text-sm font-semibold text-neutral-800">{statement}</p>
      {guidance && (
        <p className="mt-1 text-xs text-neutral-500 max-w-xs">{guidance}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

EmptyState.defaultProps = {
  variant: 'rail',
  glyph: 'records',
  statement: 'There are no items.',
  guidance: '',
  action: null,
  className: '',
};

EmptyState.propTypes = {
  variant: PropTypes.oneOf(['rail', 'detail']),
  glyph: PropTypes.oneOf(['records', 'results']),
  statement: PropTypes.string,
  guidance: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string,
};

export default EmptyState;
